/**
 * CR-6446: sectioned layout for the Purchasing → PO Headers filter panel.
 *
 * For both Supplier View (SO Headers) and Customer View (PO Headers):
 *   - FilterSection components with icons (matching Deliveries tab pattern)
 *   - Horizontal dividers between sections using SectionDivider
 *   - Footer with Clear all / Apply filters buttons is in the parent component
 *
 * Input types are preserved exactly as the ticket requires.
 *
 * CR-6439: Added cascade filter logic for dependent filters:
 *   - Supplier Group → Supplier Site
 *   - Commodity Group → Commodity Type
 */
import { useMemo } from 'react';
import { Calendar, CalendarRange, Building2, Truck, Users, FileText, Tags } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { FilterSection, FilterGrid, FilterField, filterControlClass } from '../../../filters/FilterSection';
import { DATE_RANGE_PRESETS, matchDateRangePreset } from '../../../filters/dateRangePresets';
import { cn } from '../../../../lib/utils';
import { usePOHeaderFilterCascades, POHeaderFilterOptions } from './usePOHeaderFilterCascades';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

interface FilterOption { value: string; label: string }

/** Purchase-order statuses, mirroring PurchaseOrder.STATUS_CHOICES. */
const STATUS_OPTIONS: FilterOption[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'closed', label: 'Closed' },
];

/** CR-6679: Header-level acknowledgement status options. */
const ACK_STATUS_OPTIONS: FilterOption[] = [
  { value: 'fully_acknowledged', label: 'Fully Acknowledged' },
  { value: 'partially_acknowledged', label: 'Partially Acknowledged' },
  { value: 'not_acknowledged', label: 'Not Acknowledged' },
  { value: 'no_open_lines', label: 'No Open Lines' },
];

export interface POHeaderFilterSectionsProps {
  filters: {
    poNumber: string;
    supplier: string;
    customer: string;
    status: string;
    dateFrom: string;
    dateTo: string;
    site: string;
    /** CR-6446: Warehouse filter for Site → Warehouse cascade. */
    warehouse: string;
    division: string;
    companyCountry: string;
    supplierGroup: string;
    supplierCountry: string;
    commodityGroup: string;
    commodityType: string;
    pdfAttached: string;
    strategic: string;
    ackStatus: string;  // CR-6679: Header-level acknowledgement status filter
  };
  textInputs: { poNumber: string; supplier: string; customer: string };
  siteOptions: FilterOption[];
  /** CR-6446: Warehouse options for Site → Warehouse cascade. */
  warehouseOptions?: FilterOption[];
  supplierGroupOptions: FilterOption[];
  supplierSiteOptions: FilterOption[];
  customerOptions?: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  /** CR-6439: Division options for cascade filtering */
  divisionOptions?: FilterOption[];
  /** CR-6439: Site country options for cascade filtering */
  siteCountries?: string[];
  /** CR-6439: Supplier country options for cascade filtering */
  supplierCountries?: string[];
  /**
   * CR-6439: Raw filter data for cascade filtering.
   * When provided, cascade logic filters dependent dropdowns automatically.
   */
  rawFilterOptions?: POHeaderFilterOptions | null;
  onFilterChange: (key: string, value: string) => void;
  /** CR-6446: Called when Enter is pressed on text inputs to apply filters. */
  onApply?: () => void;
  /** CR-6446: Whether this is supplier view (SO Headers) vs customer view (PO Headers). */
  isSupplier?: boolean;
  now?: Date;
}

export function POHeaderFilterSections({
  filters,
  textInputs,
  siteOptions,
  warehouseOptions: warehouseOptionsProp = [],
  supplierGroupOptions,
  supplierSiteOptions,
  customerOptions: _customerOptions = [],
  commodityGroupOptions,
  commodityTypeOptions,
  divisionOptions = [],
  siteCountries = [],
  supplierCountries = [],
  rawFilterOptions,
  onFilterChange,
  onApply,
  isSupplier = false,
  now,
}: POHeaderFilterSectionsProps) {
  // CR-6439: Use cascade hook for dependent filter logic
  const {
    siteOptions: cascadedSiteOptions,
    warehouseOptions: cascadedWarehouseOptions,
    supplierSiteOptions: cascadedSupplierOptions,
    commodityTypeOptions: cascadedCommodityTypes,
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierCountryChange,
    handleSupplierGroupChange,
    handleCommodityGroupChange,
  } = usePOHeaderFilterCascades(filters, rawFilterOptions ?? null, onFilterChange);

  // CR-6439: Use cascaded options when raw data is available AND has data,
  // otherwise fall back to props for backward compatibility.
  const effectiveSiteOptions = useMemo(() => {
    // Only use cascaded options if we have raw site data with group/country info
    if (rawFilterOptions && rawFilterOptions.sites.length > 0) {
      return cascadedSiteOptions.map((s) => ({ value: String(s.id), label: s.name }));
    }
    return siteOptions;
  }, [rawFilterOptions, cascadedSiteOptions, siteOptions]);

  // CR-6446: Warehouse options cascaded by Site
  const effectiveWarehouseOptions = useMemo(() => {
    if (rawFilterOptions && rawFilterOptions.warehouses?.length > 0) {
      return cascadedWarehouseOptions.map((w) => ({ value: String(w.id), label: w.name }));
    }
    return warehouseOptionsProp;
  }, [rawFilterOptions, cascadedWarehouseOptions, warehouseOptionsProp]);

  const effectiveSupplierOptions = useMemo(() => {
    // Only use cascaded options if we have raw supplier data with group info
    if (rawFilterOptions && rawFilterOptions.suppliers.length > 0) {
      return cascadedSupplierOptions.map((s) => ({ value: s.name, label: s.name }));
    }
    return supplierSiteOptions;
  }, [rawFilterOptions, cascadedSupplierOptions, supplierSiteOptions]);

  const effectiveCommodityTypeOptions = useMemo(() => {
    // Only use cascaded options if we have raw commodity type data
    if (rawFilterOptions && rawFilterOptions.commodity_types.length > 0) {
      return cascadedCommodityTypes.map((t) => ({ value: String(t.id), label: t.name }));
    }
    // CR-6439: Fall back to props when rawFilterOptions has no commodity types
    return commodityTypeOptions;
  }, [rawFilterOptions, cascadedCommodityTypes, commodityTypeOptions]);
  const reference = now ?? new Date();
  const activePreset = matchDateRangePreset(filters.dateFrom, filters.dateTo, reference);

  const applyPreset = (key: string) => {
    const preset = DATE_RANGE_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    if (activePreset === key) {
      onFilterChange('dateFrom', '');
      onFilterChange('dateTo', '');
      return;
    }
    const range = preset.resolve(reference);
    onFilterChange('dateFrom', range.from);
    onFilterChange('dateTo', range.to);
  };

  const orderNumberLabel = isSupplier ? 'SO Number' : 'PO Number';
  const orderNumberPlaceholder = isSupplier ? 'Enter SO Number...' : 'Enter PO Number...';

  // CR-6446: Supplier View - FilterSection layout with horizontal dividers (matching Deliveries tab)
  if (isSupplier) {
    return (
      <div className="[&>section]:border-t-0">
        {/* Divider above first section - matching Deliveries tab pattern */}
        <SectionDivider />

        {/* DATE RANGE - quick presets and date inputs */}
          <FilterSection title="Date Range" icon={Calendar}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              {/* Quick date range presets */}
              <div className="flex flex-wrap gap-2 pb-1">
                {DATE_RANGE_PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    aria-pressed={activePreset === preset.key}
                    onClick={() => applyPreset(preset.key)}
                    className={cn(
                      "px-3 py-1.5 text-xs font-medium border rounded-md transition-colors",
                      activePreset === preset.key
                        ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                        : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              {/* Date From / Date To inputs */}
              <div className="flex gap-4">
                <div className="w-44">
                  <FilterField label="From Date">
                    <input
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                      className={filterControlClass}
                    />
                  </FilterField>
                </div>
                <div className="w-44">
                  <FilterField label="To Date">
                    <input
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => onFilterChange('dateTo', e.target.value)}
                      className={filterControlClass}
                    />
                  </FilterField>
                </div>
              </div>
            </div>
          </FilterSection>

          <SectionDivider />

          {/* COMPANY section */}
          <FilterSection title="Company" icon={Building2}>
            <FilterGrid>
              <FilterField label="Site">
                <SearchableSelect
                  options={effectiveSiteOptions}
                  value={filters.site}
                  onChange={(val) => handleSiteChange(val)}
                  placeholder="All Company Sites"
                />
              </FilterField>
              <FilterField label="Division">
                <select
                  value={filters.division}
                  onChange={(e) => handleDivisionChange(e.target.value)}
                  className={filterControlClass}
                >
                  <option value="">All Divisions</option>
                  {divisionOptions.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Country">
                <select
                  value={filters.companyCountry}
                  onChange={(e) => handleCompanyCountryChange(e.target.value)}
                  className={filterControlClass}
                >
                  <option value="">All Countries</option>
                  {siteCountries.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Warehouse">
                <select
                  value={filters.warehouse}
                  onChange={(e) => onFilterChange('warehouse', e.target.value)}
                  className={filterControlClass}
                >
                  <option value="">All Warehouses</option>
                  {effectiveWarehouseOptions.map((w) => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
              </FilterField>
            </FilterGrid>
          </FilterSection>

          <SectionDivider />

          {/* SUPPLIER section */}
          <FilterSection title="Supplier" icon={Truck}>
            <FilterGrid>
              <FilterField label="Group">
                <SearchableSelect
                  options={supplierGroupOptions}
                  value={filters.supplierGroup}
                  onChange={(val) => handleSupplierGroupChange(val)}
                  placeholder="All Supplier Groups"
                />
              </FilterField>
              <FilterField label="Site">
                <SearchableSelect
                  options={effectiveSupplierOptions}
                  value={textInputs.supplier}
                  onChange={(val) => onFilterChange('supplier', val)}
                  placeholder="All Supplier Sites"
                />
              </FilterField>
            </FilterGrid>
            <div className="flex items-center gap-3 mt-4">
              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                <input
                  type="checkbox"
                  checked={filters.strategic === 'yes'}
                  onChange={(e) => onFilterChange('strategic', e.target.checked ? 'yes' : '')}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                Strategic suppliers only
              </label>
            </div>
          </FilterSection>

          <SectionDivider />

          {/* ORDER DETAILS section */}
          <FilterSection title="Order Details" icon={FileText}>
            <FilterGrid>
              <FilterField label={orderNumberLabel}>
                <input
                  type="text"
                  placeholder={orderNumberPlaceholder}
                  value={textInputs.poNumber}
                  onChange={(e) => onFilterChange('poNumber', e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onApply?.(); }}
                  className={filterControlClass}
                />
              </FilterField>
              <FilterField label="Status">
                <select
                  value={filters.status}
                  onChange={(e) => onFilterChange('status', e.target.value)}
                  className={filterControlClass}
                >
                  <option value="">All statuses</option>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="PDF Version Attached">
                <select
                  value={filters.pdfAttached}
                  onChange={(e) => onFilterChange('pdfAttached', e.target.value)}
                  className={filterControlClass}
                >
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </FilterField>
            </FilterGrid>
          </FilterSection>
      </div>
    );
  }

  // Customer View (PO Headers) - matching Deliveries tab layout
  return (
    <div className="[&>section]:border-t-0">
      {/* Divider above first section - matching Deliveries tab pattern */}
      <SectionDivider />

      {/* DATE RANGE section */}
      <FilterSection title="Date Range" icon={CalendarRange}>
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex flex-wrap gap-2 max-w-2xl">
            {DATE_RANGE_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                aria-pressed={activePreset === preset.key}
                onClick={() => applyPreset(preset.key)}
                className={`px-4 py-2 text-sm font-medium border rounded-md transition-colors ${
                  activePreset === preset.key
                    ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                    : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="w-44">
              <FilterField label="From Date">
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                  className={filterControlClass}
                />
              </FilterField>
            </div>
            <div className="w-44">
              <FilterField label="To Date">
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => onFilterChange('dateTo', e.target.value)}
                  className={filterControlClass}
                />
              </FilterField>
            </div>
          </div>
        </div>
      </FilterSection>

      <SectionDivider />

      {/* COMPANY section - CR-6439: Added Country, Division, Site with cascade filtering */}
      {/* CR-6446: Added Warehouse with Site → Warehouse cascade */}
      <FilterSection title="Company" icon={Building2}>
        <FilterGrid>
          <FilterField label="Country">
            <select
              value={filters.companyCountry}
              onChange={(e) => handleCompanyCountryChange(e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Countries</option>
              {siteCountries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Division">
            <select
              value={filters.division}
              onChange={(e) => handleDivisionChange(e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Divisions</option>
              {divisionOptions.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Site">
            <SearchableSelect
              options={effectiveSiteOptions}
              value={filters.site}
              onChange={(val) => handleSiteChange(val)}
              placeholder="All sites"
            />
          </FilterField>
          {/* CR-6446: Warehouse filter with Site → Warehouse cascade */}
          <FilterField label="Warehouse">
            <select
              value={filters.warehouse}
              onChange={(e) => onFilterChange('warehouse', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Warehouses</option>
              {effectiveWarehouseOptions.map((w) => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
          </FilterField>
        </FilterGrid>
      </FilterSection>

      <SectionDivider />

      {/* SUPPLIER section - CR-6439: Added Country with cascade filtering */}
      <FilterSection title="Supplier" icon={Users}>
        <FilterGrid>
          <FilterField label="Country">
            <select
              value={filters.supplierCountry}
              onChange={(e) => handleSupplierCountryChange(e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Countries</option>
              {supplierCountries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Group">
            <SearchableSelect
              options={supplierGroupOptions}
              value={filters.supplierGroup}
              onChange={(val) => handleSupplierGroupChange(val)}
              placeholder="All supplier groups"
            />
          </FilterField>
          {/* CR-7232: bound to textInputs.supplier, the slot onFilterChange
              actually writes and the toolbar box shares. It read
              filters.supplier before, which nothing but the ?supplier= deep
              link set — so picking a supplier here filtered the grid but left
              this select showing "All suppliers". */}
          <FilterField label="Supplier">
            <SearchableSelect
              options={effectiveSupplierOptions}
              value={textInputs.supplier}
              onChange={(val) => onFilterChange('supplier', val)}
              placeholder="All suppliers"
            />
          </FilterField>
        </FilterGrid>
        <div className="flex items-center gap-3 mt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.strategic === 'yes'}
              onChange={(e) => onFilterChange('strategic', e.target.checked ? 'yes' : '')}
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium">Strategic suppliers only</span>
          </label>
        </div>
      </FilterSection>

      <SectionDivider />

      {/* ORDER DETAILS section */}
      <FilterSection title="Order Details" icon={FileText}>
        <FilterGrid>
          <FilterField label={orderNumberLabel}>
            <input
              type="text"
              placeholder={orderNumberPlaceholder}
              value={textInputs.poNumber}
              onChange={(e) => onFilterChange('poNumber', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onApply?.(); }}
              className={filterControlClass}
            />
          </FilterField>
          <FilterField label="Status">
            <select
              value={filters.status}
              onChange={(e) => onFilterChange('status', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="PDF Version Attached">
            <select
              value={filters.pdfAttached}
              onChange={(e) => onFilterChange('pdfAttached', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </FilterField>
          {/* CR-6679: Header-level acknowledgement status filter */}
          <FilterField label="Ack Status">
            <select
              value={filters.ackStatus}
              onChange={(e) => onFilterChange('ackStatus', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All</option>
              {ACK_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FilterField>
        </FilterGrid>
      </FilterSection>

      {/* COMMODITY section (legacy, shown only if options exist) */}
      {(commodityGroupOptions.length > 0 || effectiveCommodityTypeOptions.length > 0) && (
        <>
          <SectionDivider />
          <FilterSection title="Commodity" icon={Tags}>
          <FilterGrid>
            <FilterField label="Commodity Group">
              <select
                value={filters.commodityGroup}
                onChange={(e) => handleCommodityGroupChange(e.target.value)}
                className={filterControlClass}
              >
                <option value="">All commodity groups</option>
                {commodityGroupOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Commodity Type">
              <select
                value={filters.commodityType}
                onChange={(e) => onFilterChange('commodityType', e.target.value)}
                className={filterControlClass}
              >
                <option value="">All commodity types</option>
                {effectiveCommodityTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </FilterField>
          </FilterGrid>
        </FilterSection>
        </>
      )}
    </div>
  );
}
